"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode } from "react";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import { type AiModelAccess } from "@/lib/ai/model-access";
import { ModelId } from "@/lib/ai/models";
import type { AnalysisTargetOptionsState } from "./analysis-target-options";
import {
  ChatPromptSurface,
  ChatPromptSurfaceInput,
  ChatPromptSurfaceSuggestions,
  ChatPromptSurfaceTitle,
} from "./chat-prompt-surface";
import type { ChatQuotaTierIndicatorState } from "./chat-quota-view-state";
import type { ChatSendInput } from "./chat-send-input";
import { useSamplePrompts } from "./use-sample-prompts";

const HIDDEN_QUOTA_INDICATOR: ChatQuotaTierIndicatorState = {
  kind: "hidden",
};

type Props = {
  onStart?: (
    message: ChatSendInput,
  ) => boolean | void | Promise<boolean | void>;
  model: ModelId;
  aiModelAccess: AiModelAccess;
  onModelChange: (model: ModelId) => void;
  compactWhenInactive?: boolean;
  isCurrentModelQuotaExceeded?: boolean;
  statusCard?: ReactNode;
  lowQuotaIndicator?: ChatQuotaTierIndicatorState;
  highQuotaIndicator?: ChatQuotaTierIndicatorState;
  pathname: string;
  optionsState: AnalysisTargetOptionsState;
  suppressPageContextAutoSyncOnCurrentPath: () => void;
};

/**
 * 新規チャット開始画面の入力 UI。
 */
export function ChatStarter({
  onStart,
  model,
  aiModelAccess,
  onModelChange,
  compactWhenInactive = true,
  isCurrentModelQuotaExceeded = false,
  statusCard,
  lowQuotaIndicator = HIDDEN_QUOTA_INDICATOR,
  highQuotaIndicator = HIDDEN_QUOTA_INDICATOR,
  pathname,
  optionsState,
  suppressPageContextAutoSyncOnCurrentPath,
}: Props) {
  const { t } = useLingui();
  const { workspaceId, lang } = useAiChatUiContext();
  const {
    prompts: samplePrompts,
    isGenerating,
    refresh: refreshPrompts,
  } = useSamplePrompts({
    workspaceId,
    lang: lang ?? "en",
    optionsState,
  });

  return (
    <ChatPromptSurface
      onSubmit={(message) => onStart?.(message)}
      model={model}
      aiModelAccess={aiModelAccess}
      onModelChange={onModelChange}
      pathname={pathname}
      optionsState={optionsState}
      suppressPageContextAutoSyncOnCurrentPath={
        suppressPageContextAutoSyncOnCurrentPath
      }
      submitBlocked={isCurrentModelQuotaExceeded}
      compactWhenInactive={compactWhenInactive}
      lowQuotaIndicator={lowQuotaIndicator}
      highQuotaIndicator={highQuotaIndicator}
      className="mx-auto w-full max-w-3xl space-y-4 transition-[margin] duration-200 ease-out"
      compactClassName="space-y-2.5"
    >
      <ChatPromptSurfaceTitle
        className="text-center font-bold transition-[font-size,margin] duration-200"
        expandedClassName="mb-8 text-3xl"
        compactClassName="mb-4 text-2xl"
      >
        <Trans>Ask anything about your website</Trans>
      </ChatPromptSurfaceTitle>
      {statusCard}
      <ChatPromptSurfaceInput
        disclaimerPlacement="after-input"
        hideControlsWhenCompact
        placeholder={t`Can you tell me which pages have seen an increase in traffic compared to last month?`}
        textareaClassName="text-[15px]"
        expandedTextareaClassName="min-h-24 py-2.5 leading-6"
        compactTextareaClassName="leading-5"
        expandedToolbarClassName="pt-0.5 pb-2.5"
        compactToolbarClassName="pt-1"
      />
      <ChatPromptSurfaceSuggestions
        prompts={samplePrompts}
        isGenerating={isGenerating}
        onRefresh={refreshPrompts}
        className="text-left transition-[padding,opacity] duration-200"
        expandedClassName="space-y-4 pt-4"
        compactClassName="space-y-2 pt-2 opacity-80"
      />
    </ChatPromptSurface>
  );
}
